import { useState } from "react";
import { Download, ImageSquare, Copy, Package, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api } from "../lib/api";

// 🔴 RÈGLE DE MARQUE OFFICIELLE (rectification Kru Ralph — juin 2026) :
// « INSTITUT IMPACT ÉLITE MARTIAL » et « KAIZEN SYSTEM FRANCHISE » vont
// TOUJOURS ENSEMBLE (co-branding). Identité : premium, internationale, épurée,
// élite, intemporelle. Ne plus jamais présenter les marques de façon isolée.

const INSTITUT_BASE = "/logos/institut_impact";
const KAIZEN_BASE = "/logos/kaizen_system/generated";

// Déclinaisons par discipline — chaque emblème co-signe Institut Impact × Kaizen System.
const INSTITUT_LOGOS = [
  { file: "iiem_master_wordmark_disciplines_transparent.png", title: "★ Wordmark + disciplines", usage: "Logo principal réunissant le nom + les 5 disciplines. Site, affiches, certificats, bannière.", priority: "P0" },
  { file: "iiem_emblem_muaythai_transparent.png", title: "Emblème MUAY THAÏ", usage: "Programmes Muay Thaï, t-shirts, certificats.", priority: "P1" },
  { file: "iiem_emblem_bjj_rdjp_transparent.png", title: "Emblème BJJ RDJP", usage: "Programmes Brazilian Jiu-Jitsu, gi, certificats.", priority: "P1" },
  { file: "iiem_emblem_kickboxing_transparent.png", title: "Emblème KICKBOXING", usage: "Programmes Kickboxing, shorts, t-shirts.", priority: "P1" },
  { file: "iiem_emblem_canryu_transparent.png", title: "Emblème JIU-JITSU CAN-RYU", usage: "Programmes Can-Ryu (加流術), keikogi, certificats.", priority: "P1" },
];

// Déclinaisons « système / franchise » — à utiliser conjointement au co-branding.
const KAIZEN_LOGOS = [
  { file: "kaizen_system_master_color_transparent.png", title: "Master COULEUR (transparent)", usage: "Documents franchise, présentations, supports premium.", priority: "P0" },
  { file: "kaizen_system_master_white_on_black.png", title: "Master BLANC sur NOIR", usage: "Supports sombres, gilet franchisé, slides.", priority: "P1" },
  { file: "kaizen_system_master_red_on_white.png", title: "Master ROUGE/NOIR sur BLANC", usage: "Documents papier, en-têtes.", priority: "P1" },
  { file: "kaizen_system_icon_only_enso_K.png", title: "ICON seul (enso + K)", usage: "Favicon, badge, watermark.", priority: "P1" },
  { file: "kaizen_system_banner_16x9_hub_franchise.png", title: "BANNIÈRE 16:9 Hub Franchise", usage: "Présentations & couvertures réseaux.", priority: "P1" },
];

function LogoCard({ base, l }) {
  async function copyLink() {
    const url = `${window.location.origin}${base}/${l.file}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`URL copiée : ${l.file}`);
    } catch {
      toast.error("Impossible de copier");
    }
  }
  return (
    <div data-testid={`logo-gallery-card-${l.file}`} className="rounded-lg bg-[#1A1A1A]/60 border border-white/10 hover:border-[#C5A059]/50 transition-colors duration-300 overflow-hidden flex flex-col">
      <div className="aspect-square bg-[#0A0A0A] flex items-center justify-center p-6 border-b border-white/10" style={{ backgroundImage: "linear-gradient(45deg,#161616 25%,transparent 25%),linear-gradient(-45deg,#161616 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#161616 75%),linear-gradient(-45deg,transparent 75%,#161616 75%)", backgroundSize: "22px 22px", backgroundPosition: "0 0,0 11px,11px -11px,-11px 0px" }}>
        <img src={`${base}/${l.file}`} alt={l.title} className="max-h-full max-w-full object-contain" loading="lazy" />
      </div>
      <div className="p-5 flex-1 flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${l.priority === "P0" ? "bg-[#D90429]/20 text-[#ff6b7e]" : "bg-[#C5A059]/20 text-[#C5A059]"}`}>{l.priority}</span>
          <h3 className="text-sm font-bold text-[#F8F5F2] flex-1">{l.title}</h3>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">{l.usage}</p>
        <code className="text-[10px] text-zinc-500 bg-[#0A0A0A] px-2 py-1 rounded font-mono truncate">{l.file}</code>
        <div className="flex gap-2 mt-auto pt-2">
          <a href={`${base}/${l.file}`} download={l.file} data-testid={`logo-download-button-${l.file}`}
            className="flex-1 px-3 py-2 rounded bg-emerald-500/10 border border-emerald-700/40 text-emerald-300 text-xs font-bold uppercase tracking-widest hover:bg-emerald-500/20 transition flex items-center justify-center gap-1.5">
            <Download size={12} weight="fill" /> Télécharger
          </a>
          <button onClick={copyLink} data-testid={`btn-copy-${l.file}`}
            className="px-3 py-2 rounded bg-[#0A0A0A] border border-white/10 text-zinc-400 hover:text-[#C5A059] text-xs font-bold uppercase tracking-widest transition flex items-center justify-center gap-1.5">
            <Copy size={12} /> URL
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KaizenLogosGallery() {
  const [tab, setTab] = useState("institut");
  const [packBusy, setPackBusy] = useState(false);

  async function downloadBrandPack() {
    setPackBusy(true);
    try {
      const { data } = await api.get("/brand-pack/download", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "pack-marque-kaizen-impact.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("📦 Pack de marque téléchargé (PNG + SVG + favicon + polices)");
    } catch {
      toast.error("Échec de la génération du pack de marque");
    }
    setPackBusy(false);
  }

  function downloadAll(list, base) {
    list.forEach((l, i) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = `${base}/${l.file}`;
        a.download = l.file;
        a.click();
      }, i * 400);
    });
    toast.success(`📦 Téléchargement de ${list.length} logos…`);
  }

  const isInstitut = tab === "institut";
  const list = isInstitut ? INSTITUT_LOGOS : KAIZEN_LOGOS;
  const base = isInstitut ? INSTITUT_BASE : KAIZEN_BASE;

  return (
    <div className="min-h-full bg-[#0A0A0A] p-6 lg:p-10" data-testid="kaizen-logos-gallery">
      <div className="max-w-7xl mx-auto space-y-10">
        <header className="border-b border-white/10 pb-6">
          <div className="flex items-center gap-2 text-[#D90429]">
            <ImageSquare size={20} weight="fill" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-bold">Identité officielle</span>
          </div>
          <div className="flex items-end justify-between gap-6 flex-wrap mt-2">
            <div>
              <h1 className="brand-serif text-4xl sm:text-5xl font-semibold text-[#F8F5F2] tracking-wide">Co-branding & logos</h1>
              <p className="text-sm text-zinc-400 mt-3 max-w-2xl leading-relaxed">
                <strong className="text-[#F8F5F2]">Institut Impact Élite Martial</strong> et <strong className="text-[#F8F5F2]">Kaizen System</strong> s'affichent <strong className="text-[#C5A059]">toujours ensemble</strong>. Les racines (serif) rencontrent l'évolution (sans) — premium, international, intemporel.
              </p>
            </div>
            <button onClick={downloadBrandPack} disabled={packBusy} data-testid="brand-pack-download-btn"
              className="shrink-0 inline-flex items-center gap-2.5 px-6 py-3.5 rounded-lg bg-[#C5A059] hover:bg-[#A68641] text-[#0A0A0A] text-sm font-bold uppercase tracking-widest transition-colors disabled:opacity-60">
              {packBusy ? <CircleNotch size={18} className="animate-spin" /> : <Package size={18} weight="fill" />}
              {packBusy ? "Génération…" : "Télécharger le pack de marque"}
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">ZIP : lockups PNG (clair + sombre) · SVG vectoriels · favicon · polices officielles · charte d'usage.</p>
        </header>

        {/* === Lockup officiel co-brandé (rendu live, versions sombre + claire) === */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="relative flex flex-col items-center justify-center p-12 rounded-lg border border-white/10 bg-[#0A0A0A] group">
            <span className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.3em] font-bold text-zinc-500">Sur fond sombre</span>
            <Logo variant="static" tone="light" />
          </div>
          <div className="relative flex flex-col items-center justify-center p-12 rounded-lg border border-white/10 bg-[#F8F5F2] group">
            <span className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.3em] font-bold text-zinc-500">Sur fond clair</span>
            <Logo variant="static" tone="dark" />
          </div>
        </section>

        {/* === Déclinaisons (assets téléchargeables) === */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <nav className="flex gap-2 border-b border-white/10 overflow-x-auto">
            {[
              { k: "institut", label: "Institut Impact — déclinaisons" },
              { k: "kaizen", label: "Kaizen System — déclinaisons" },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`filter-${t.k}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition whitespace-nowrap ${
                  tab === t.k ? "text-[#C5A059] border-[#C5A059]" : "text-zinc-500 border-transparent hover:text-zinc-300"
                }`}>
                {t.label}
              </button>
            ))}
          </nav>
          <button onClick={() => downloadAll(list, base)} data-testid="btn-download-all"
            className="px-4 py-2 rounded-lg bg-[#C5A059] hover:bg-[#A68641] text-[#0A0A0A] text-xs font-bold uppercase tracking-widest transition flex items-center gap-2 shrink-0">
            <Download size={14} weight="fill" /> Télécharger ce groupe
          </button>
        </div>

        <div className="p-4 rounded-lg bg-[#1A1A1A]/60 border border-white/10 text-sm">
          <p className="text-zinc-300 text-xs leading-relaxed">
            Ces déclinaisons sont des <strong className="text-[#F8F5F2]">composantes</strong> du système de marque. Sur tout support public (affiches, certificats, réseaux), elles doivent rester associées au co-branding <strong className="text-[#C5A059]">Institut Impact × Kaizen System</strong> — jamais isolées.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map(l => <LogoCard key={l.file} base={base} l={l} />)}
        </div>
      </div>
    </div>
  );
}
